import { CurrentUser } from '@appTypes/CurrentUser';
import { DealerState } from '@appTypes/DealerState';
import { UserProfileFormResource } from '@appTypes/header/UserProfile';
import { Icon, Typography } from '@components/style';
import { useCheckDevice } from '@hooks/UseCheckDevice';
import { UsersService } from '@services/users/UsersService';
import { ErrorToast } from '@services/utils/ToastUtils';
import { AppState } from '@store/AppState';
import { theme } from '@theme';
import { Dispatch, FC, SetStateAction, useEffect, useRef, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useSelector } from 'react-redux';
import styled from 'styled-components';
import { RowDivider } from '../UserProfileStyles';

type UserProfilePhoneNumberProps = {
  isOtpSubmittedAndVerified?: boolean;
  setIsOtpSubmittedAndVerified?: Dispatch<SetStateAction<boolean>>;
  displayPhoneNumber: string;
  setDisplayPhoneNumber: Dispatch<SetStateAction<string>>;
};

const UserProfilePhoneNumber: FC<UserProfilePhoneNumberProps> = ({
  isOtpSubmittedAndVerified,
  setIsOtpSubmittedAndVerified,
  displayPhoneNumber,
  setDisplayPhoneNumber,
}) => {
  const { featureToggle } = useSelector<AppState, DealerState>((state) => state.dealer);
  const mobileNotifications = featureToggle?.mobileNotifications;
  const { isMobileDevice } = useCheckDevice();
  const [isPhoneInputValid, setIsPhoneInputValid] = useState(false);
  const [doesPhoneExist, setDoesPhoneExists] = useState(false);
  const [showOtpSection, setShowOtpSection] = useState(false);
  const [otp, setOtp] = useState('');
  const [isOtpInputValid, setIsOtpInputValid] = useState(false);
  const [otpError, setOtpError] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [isCancelled, setIsCancelled] = useState(false);
  const [isOtpExpired, setIsOtpExpired] = useState(false);
  const [isOtpSent, setIsOtpSent] = useState(false);
  const [showResendOtpButton, setShowResendOtpButton] = useState(false);
  const [otpVerificationCode, setOtpVerificationCode] = useState('');
  const [displayMessage, setDisplayMessage] = useState('');
  const [isUnexpectedErrorOccured, setIsUnexpectedErrorOccured] = useState(false);
  const [enableRequestCode, setEnableRequestCode] = useState(false);
  const [showCancelButton, setShowCancelButton] = useState(false);
  const [showEditWrapper, setShowEditWrapper] = useState(false);
  const timeoutIdRef = useRef(null);
  const { getValues, setValue, register } = useFormContext<UserProfileFormResource>();
  const currentUser = useSelector<AppState, CurrentUser>((state) => state.currentUser);
  const permissions = currentUser.permissions;

  useEffect(() => {
    if (!getValues('phoneNumber')) {
      setDoesPhoneExists(false);
    } else {
      setDoesPhoneExists(true);
      setDisplayPhoneNumber(formatPhoneNumber(getValues('phoneNumber')));
    }
  }, []);

  const formatPhoneNumber = (input: string) => {
    const part1 = input.substring(0, 3);
    const part2 = input.substring(3, 6);
    const part3 = input.substring(6, 10);

    if (input.length > 6) {
      return `${part1}-${part2}-${part3}`;
    } else if (input.length > 3) {
      return `${part1}-${part2}`;
    } else {
      return part1;
    }
  };

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const input = event.target.value.replace(/\D/g, '');
    const formattedPhoneNumber = formatPhoneNumber(input);

    setDisplayPhoneNumber(formattedPhoneNumber);
    setIsPhoneInputValid(input.length === 10);
    setEnableRequestCode(input.length === 10 && input !== getValues('phoneNumber'));
  };

  const generateAndSendOtpSms = async () => {
    try {
      const response = await UsersService.generateAndSendOtpSms(
        displayPhoneNumber.replace(/\D/g, ''),
      );
      const verificationCode = await response.text();
      setOtpVerificationCode(verificationCode);
      return true;
    } catch (error) {
      ErrorToast(`'An error has occurred: ${error}'`);
      return false;
    }
  };

  const handleRequestCode = async () => {
    const otpSent = await generateAndSendOtpSms();
    if (!otpSent) {
      return;
    }
    setIsOtpInputValid(false);
    setIsUnexpectedErrorOccured(false);
    setIsCancelled(false);
    setOtp('');
    setShowOtpSection(true);
    setIsOtpSent(true);
    setOtpError(false);
    setIsOtpExpired(false);
    setDisplayMessage(
      'The one-time code has been sent to the Mobile Number. You can request the code again in 3 minutes.',
    );
    setShowResendOtpButton(false);
    if (timeoutIdRef.current) {
      clearTimeout(timeoutIdRef.current);
    }
    timeoutIdRef.current = setTimeout(() => {
      setShowResendOtpButton(true);
    }, 180000);
  };

  const handleOtpChange = (event: React.ChangeEvent<HTMLInputElement> | any) => {
    if (isNaN(event.target.value) || event.target.value === ' ') return;
    const input = event.target.value.replace(/\D/g, '');
    setOtp(input);
    setOtpError(false);
    setIsOtpExpired(false);
    setIsUnexpectedErrorOccured(false);
    setIsOtpSent(false);
    setIsOtpInputValid(input.length === 6);
    setShowResendOtpButton(false);
  };

  const handleOtpSubmit = async () => {
    try {
      const response = await UsersService.validateOtp(otpVerificationCode, otp);
      if (response.status === 200) {
        setOtp('');
        setOtpError(false);
        setIsOtpExpired(false);
        setIsUnexpectedErrorOccured(false);
        setShowOtpSection(false);
        setShowResendOtpButton(false);
        setValue('phoneNumber', displayPhoneNumber.replace(/\D/g, ''));
        setIsOtpSubmittedAndVerified(true);
        setShowEditWrapper(true);
      }
    } catch (error) {
      if (error.toString().includes('400')) {
        setOtpError(true);
        setIsOtpExpired(false);
        setIsUnexpectedErrorOccured(false);
      } else if (error.toString().includes('403')) {
        setIsOtpExpired(true);
        setOtpError(false);
        setIsUnexpectedErrorOccured(false);
      } else {
        setIsUnexpectedErrorOccured(true);
        setOtpError(false);
        setIsOtpExpired(false);
        ErrorToast(`'An error has occurred: ${error}'`);
      }
    }
    setIsOtpSent(false);
  };

  const handleEditClick = () => {
    if (displayPhoneNumber.replace(/\D/g, '') === getValues('phoneNumber')) {
      setIsPhoneInputValid(true);
      setEnableRequestCode(false);
    }
    setShowCancelButton(true);
    setIsEditing(true);
    setShowEditWrapper(false);
  };

  const handleCancelClick = () => {
    if (isEditing && showCancelButton) {
      setIsEditing(false);
      if (isOtpSubmittedAndVerified || doesPhoneExist) {
        setShowEditWrapper(true);
        setDisplayPhoneNumber(formatPhoneNumber(getValues('phoneNumber')));
      }
    } else {
      setDisplayPhoneNumber('');
      setEnableRequestCode(false);
    }
    setShowCancelButton(false);
    setOtpError(false);
    setIsOtpExpired(false);
    setIsUnexpectedErrorOccured(false);
    setIsCancelled(true);
    setShowOtpSection(false);
    setIsOtpInputValid(false);
  };

  const handleRequestOtpAgain = async () => {
    const otpSent = await generateAndSendOtpSms();
    setIsOtpInputValid(false);
    setOtpError(false);
    setIsOtpExpired(false);
    setIsUnexpectedErrorOccured(false);
    if (!otpSent) {
      return;
    }
    setIsOtpSent(true);
    setDisplayMessage('One-time code sent again. You can request the code again in 3 minutes.');
    setOtp('');
    setShowResendOtpButton(false);

    if (timeoutIdRef.current) {
      clearTimeout(timeoutIdRef.current);
    }
    timeoutIdRef.current = setTimeout(() => {
      setIsOtpSent(false);
      setShowResendOtpButton(true);
    }, 180000);
  };

  return (
    <RowDivider
      borderColor={isMobileDevice ? theme.palette.backgroundGray : theme.palette.lightGreySix}
      padding={0}
    >
      {(doesPhoneExist && !isEditing) || showEditWrapper ? (
        <EditWrapper>
          <div>
            <Label>Mobile Number</Label>
          </div>
          <EditSection>
            <PhoneNumberText>{displayPhoneNumber}</PhoneNumberText>
            <LinkButton onClick={handleEditClick} marginLeft='0'>
              Edit
            </LinkButton>
            {isOtpSubmittedAndVerified && !isCancelled && (
              <Message type='success'>Mobile number verified successfully</Message>
            )}
          </EditSection>
        </EditWrapper>
      ) : (
        <Wrapper isMobileDevice={isMobileDevice}>
          <div>
            <div>
              <Label>Mobile Number</Label>
            </div>
            <InputContainer>
              <Input
                data-testid='mobile-number'
                type='text'
                value={displayPhoneNumber}
                onChange={handleInputChange}
                placeholder='123-456-7890'
                maxLength={12}
                isInputValid={isPhoneInputValid}
                disabled={showOtpSection}
                width='148'
              />
              {!showOtpSection && (
                <Button
                  type='button'
                  onClick={handleRequestCode}
                  disabled={!enableRequestCode}
                  width='115'
                >
                  Request Code
                </Button>
              )}
              {!showOtpSection && showCancelButton && (
                <LinkButton onClick={handleCancelClick}>Cancel</LinkButton>
              )}
            </InputContainer>
          </div>

          {showOtpSection && !isCancelled && (
            <div>
              <div>
                <Label>One-time Code</Label>
              </div>
              <InputContainer>
                <Input
                  data-testid='otp-input'
                  type='text'
                  value={otp}
                  onChange={handleOtpChange}
                  placeholder='XXXXXX'
                  maxLength={6}
                  isInputValid={isOtpInputValid}
                  width='135'
                />
                <Button
                  type='button'
                  onClick={handleOtpSubmit}
                  disabled={!isOtpInputValid}
                  width='70'
                >
                  Submit
                </Button>
                <LinkButton onClick={handleCancelClick}>Cancel</LinkButton>
                {(otpError || isOtpExpired || showResendOtpButton) && (
                  <LinkButton onClick={handleRequestOtpAgain}>Request Code Again</LinkButton>
                )}
              </InputContainer>
              <div>
                {otpError && <Message type='error'>Incorrect code. Please try again</Message>}
                {isOtpExpired && (
                  <Message type='error'>Code expired. Please request a new code</Message>
                )}
                {isUnexpectedErrorOccured && (
                  <Message type='error'>
                    Unable to verify code at this time. Please try again later
                  </Message>
                )}
                {isOtpSent && <Message type='success'>{displayMessage}</Message>}
              </div>
            </div>
          )}
        </Wrapper>
      )}

      {mobileNotifications && (
        <Container>
          {permissions.includes('allowSMS') ? (
            <div className='nice-checkboxes'>
              <label>
                <input
                  id='smsOptOut'
                  type='checkbox'
                  className={`${!!getValues('isOptedOutSms') ? 'checked' : ''}`}
                  disabled={!getValues('phoneNumber')}
                  {...register('isOptedOutSms')}
                />
                <span>Opted Out of SMS</span>
              </label>
            </div>
          ) : (
            <>
              <Icon
                color={theme.palette.danger}
                data-testid='warning-icon'
                type={'stop'}
                size={theme.typography.fontSize14}
                marginRight={7}
              ></Icon>
              <Typography
                size={theme.typography.fontSize14}
                fontWeight={theme.typography.fontWeights.normal}
                data-testid='opt-out'
              >
                SMS Notifications Not Allowed
              </Typography>
            </>
          )}
        </Container>
      )}
    </RowDivider>
  );
};

type InputProps = {
  isInputValid?: boolean;
  width?: string;
};

type MessageProps = {
  type?: string;
};

type ButtonProps = {
  width?: string;
  disabled?: boolean;
};

type WrapperProps = {
  isMobileDevice?: boolean;
};

type ListButtonProps = {
  marginLeft?: string;
};

const Wrapper = styled.div<WrapperProps>`
  margin: 15px 0;
  gap: 2rem;
  display: flex;
  flex-direction: ${(props) => (props.isMobileDevice ? 'column' : 'row')};
`;

const EditWrapper = styled.div`
  display: flex;
  flex-direction: column;
  margin-top: 15px;
`;

const InputContainer = styled.div`
  display: flex;
  align-items: center;
`;

const EditSection = styled.div`
  display: flex;
  align-items: baseline;
  gap: 2rem;
`;

const Input = styled.input<InputProps>`
  padding: 5px;
  height: 35px;
  width: ${(props) => props.width}px;
  color: ${(props) => (props.isInputValid ? theme.palette.wetAsphalt : theme.palette.danger)};
  border: 1px solid ${theme.palette.backgroundGray};
  border-radius: 4px;
  background: ${theme.palette.white};
  font-size: ${theme.typography.fontSize16}px;
  box-shadow: inset 0 1px 1px rgba(0, 0, 0, 0.075);
  :focus {
    box-shadow:
      inset 0 1px 1px rgb(0 0 0 / 8%),
      0 0 8px rgb(85 52 140 / 60%);
    border-color: ${theme.palette.amethyst};
    outline: 0;
  }

  &::placeholder {
    color: ${theme.palette.dusty};
  }

  &:disabled {
    color: ${theme.palette.dustyTwo};
  }
`;

const PhoneNumberText = styled.p`
  font-size: ${theme.typography.fontSize16}px;
  font-weight: ${theme.typography.fontWeights.normal};
  white-space: nowrap;
`;

const Button = styled.button<ButtonProps>`
  height: 35px;
  width: ${(props) => props.width}px;
  color: ${(props) => (props.disabled ? theme.palette.lightGreyTwo : theme.palette.wetAsphalt)};
  border: 1px solid ${theme.palette.backgroundGray};
  border-radius: 3px;
  background: ${(props) =>
    props.disabled
      ? theme.palette.vehiclePreferencesGrayOutButtonColor
      : theme.palette.backgroundGray};
  font-weight: ${theme.typography.fontWeights.semibold};
  cursor: ${(props) => (props.disabled ? 'no-drop !important' : 'pointer')};
  padding: 8px;
  font-size: ${theme.typography.fontSize14}px;
  margin-left: 12px;
  line-height: 17px;
`;

const LinkButton = styled.a<ListButtonProps>`
  font-size: ${theme.typography.fontSize14}px;
  margin-left: ${(props) => (props.marginLeft ? props.marginLeft : '12px')};
  font-weight: ${theme.typography.fontWeights.semibold};
  text-decoration: none !important;
  color: ${theme.palette.linkColor};
`;

const Message = styled.p<MessageProps>`
  font-size: ${theme.typography.fontSize14}px;
  margin-top: 4px;
  font-weight: ${theme.typography.fontWeights.semibold};
  color: ${(props) => (props.type === 'success' ? theme.palette.success : theme.palette.danger)};
`;

const Label = styled.label`
  font-weight: ${theme.typography.fontWeights.semibold};
  font-size: ${theme.typography.fontSize14};
`;

const Container = styled.div`
  position: relative;
  padding-bottom: 10px;
  align-items: center;
  display: flex;
  label {
    margin-bottom: 0px;
  }
`;

export default UserProfilePhoneNumber;
